import type { FC } from 'react';

import Enums from '@core/types/api/Enums';

import { useSlabQuery } from '../../hooks/useSlabQuery';
import { type AsyncJobFailedItem, AsyncJobFailedItemsModal } from './AsyncJobFailedItemsModal';
import { AsyncJobFailedItemsPreview } from './AsyncJobFailedItemsPreview';
import type {
  AsyncJobFailureDetailsProps,
  AsyncJobFailurePreviewProps,
} from './AsyncJobStatusConfig';
import { useAsyncJobFailedObjectIds } from './useAsyncJobFailedObjectIds';

const FAILURE_DETAIL_POLL_INTERVAL_MS = 5000;

type FailedItemsResult = {
  failedCount: number;
  items: AsyncJobFailedItem[];
  isLoading: boolean;
  isError: boolean;
};

const quotePrimaryText = (quote: {
  quoteNumber: string;
  name: string | null;
  projectName: string;
}): string => {
  const quoteName = quote.name ?? quote.projectName;
  return quoteName === '' ? quote.quoteNumber : `${quote.quoteNumber} - ${quoteName}`;
};

const quoteSecondaryText = (quote: {
  companyName: string | null;
  projectName: string;
}): string | undefined => {
  const parts = [quote.companyName, quote.projectName].filter(
    (part): part is string => typeof part === 'string' && part !== '',
  );
  return parts.length === 0 ? undefined : parts.join(' | ');
};

const useQuotePricingUpdateFailedItems = ({
  jobId,
  summaryFailedCount,
  enabled,
  isProcessing,
}: {
  jobId: string;
  summaryFailedCount: number;
  enabled: boolean;
  isProcessing: boolean;
}): FailedItemsResult => {
  const {
    failedObjectIds: failedQuoteIds,
    failedObjects,
    itemsFailed: detailFailedCount,
    isLoading: isLoadingJob,
    isError: isJobError,
  } = useAsyncJobFailedObjectIds(
    jobId,
    enabled,
    enabled && isProcessing ? FAILURE_DETAIL_POLL_INTERVAL_MS : false,
  );

  const {
    data: quoteSummaries,
    isLoading: isLoadingQuotes,
    isError: isQuoteError,
  } = useSlabQuery(
    'GET quote summaries',
    {
      queryParams: {
        page: 0,
        perPage: failedQuoteIds.length,
        filterBy: [
          {
            operation: Enums.FilterOperation.In,
            name: 'id',
            values: failedQuoteIds,
          },
        ],
      },
    },
    {
      enabled: enabled && failedQuoteIds.length > 0,
    },
  );

  const reasonByQuoteId = new Map(
    failedObjects
      .filter(({ reason }) => reason !== null && reason !== '')
      .map(({ objectId, reason }) => [objectId, reason ?? '']),
  );
  const quoteById = new Map((quoteSummaries?.items ?? []).map((quote) => [quote.id, quote]));
  const items: AsyncJobFailedItem[] = failedQuoteIds.flatMap((quoteId) => {
    const quote = quoteById.get(quoteId);
    if (quote === undefined) {
      return [];
    }

    const secondaryText = quoteSecondaryText(quote);
    const reason = reasonByQuoteId.get(quote.id);

    return [
      {
        id: quote.id,
        primaryText: quotePrimaryText(quote),
        ...(secondaryText === undefined ? {} : { secondaryText }),
        ...(reason === undefined ? {} : { reason }),
        destination: `/quotes/${quote.id}`,
      },
    ];
  });
  const failedCount = Math.max(
    summaryFailedCount,
    detailFailedCount,
    failedObjects.length,
    failedQuoteIds.length,
  );

  return {
    failedCount,
    items,
    isLoading: isLoadingJob || isLoadingQuotes,
    isError: isJobError || isQuoteError,
  };
};

export const QuotePricingUpdateFailedModal: FC<AsyncJobFailureDetailsProps> = ({
  isOpen,
  onClose,
  jobId,
  summaryFailedCount,
  isProcessing,
}) => {
  const { failedCount, items, isLoading, isError } = useQuotePricingUpdateFailedItems({
    jobId,
    summaryFailedCount,
    enabled: isOpen,
    isProcessing,
  });

  return (
    <AsyncJobFailedItemsModal
      isOpen={isOpen}
      onClose={onClose}
      title='Failed to create revisions'
      description={`${failedCount} quote${failedCount === 1 ? '' : 's'} could not be updated`}
      actionLabel='Solve Issue'
      failedCount={failedCount}
      items={items}
      isLoading={isLoading}
      isError={isError}
    />
  );
};

export const QuotePricingUpdateFailedPreview: FC<AsyncJobFailurePreviewProps> = ({
  jobId,
  summaryFailedCount,
  isProcessing,
  actionLabel,
  onViewFailures,
}) => {
  const { failedCount, items, isLoading, isError } = useQuotePricingUpdateFailedItems({
    jobId,
    summaryFailedCount,
    enabled: true,
    isProcessing,
  });

  return (
    <AsyncJobFailedItemsPreview
      actionLabel={actionLabel}
      failedCount={failedCount}
      items={items}
      isLoading={isLoading}
      isError={isError}
      onViewFailures={onViewFailures}
    />
  );
};
